import * as THREE from "three";
import { RENDERABLE_LAYER } from "../../../layers";
import { Shape } from "../_base/Shape";
import { VertexHandle } from "../_shared/VertexHandle";
import { Polyline } from "../Polyline";

const OUTLINE_SEGMENTS = 64;
const OUTLINE_MATERIAL_DEFINITION: THREE.LineBasicMaterialParameters = {
	color: 0xff0000,
	linewidth: 1,
	depthTest: true,
	depthWrite: true,
};

const DEFAULT_HANDLE_COLOR: THREE.ColorRepresentation = 0x0000ff;
const DEFAULT_RADIUS_LINE_COLOR: THREE.ColorRepresentation = 0x000000;

type CircleHandle = "center" | "radius";

class Circle extends Shape {
	public readonly sceneObject = new THREE.Group();

	#centerPoint: THREE.Vector3 | null = null;
	public get centerPoint() {
		return this.#centerPoint;
	}

	#centerMarker: VertexHandle | null = null;
	public get centerMarker() {
		return this.#centerMarker;
	}

	#radiusMarker: VertexHandle | null = null;
	public get radiusMarker() {
		return this.#radiusMarker;
	}

	#radiusDirection = new THREE.Vector3(1, 0, 0);
	public get radiusDirection() {
		return this.#radiusDirection;
	}

	#radiusValue = 0;
	public get radiusValue() {
		return this.#radiusValue;
	}

	#outline: THREE.LineLoop<THREE.BufferGeometry, THREE.LineBasicMaterial> | null = null;
	public get outline() {
		return this.#outline;
	}

	#radiusLine: Polyline | null = null;
	public get radiusLine(): Polyline | null {
		return this.#radiusLine;
	}

	private outlineMaterialDefinition: THREE.LineBasicMaterialParameters;
	private readonly handleColor: string;
	private readonly radiusLineColor: THREE.ColorRepresentation;
	readonly #maxRadius: number;

	constructor({
		circleColor,
		centerMarkerColor,
		radiusLineColor,
		maxRadius,
	}: {
		circleColor?: THREE.ColorRepresentation;
		centerMarkerColor?: THREE.ColorRepresentation;
		radiusLineColor?: THREE.ColorRepresentation;
		maxRadius?: number;
	} = {}) {
		super();

		this.outlineMaterialDefinition = {
			...OUTLINE_MATERIAL_DEFINITION,
			color: circleColor ?? OUTLINE_MATERIAL_DEFINITION.color,
		};
		this.handleColor = new THREE.Color(centerMarkerColor ?? DEFAULT_HANDLE_COLOR).getStyle();
		this.radiusLineColor = radiusLineColor ?? DEFAULT_RADIUS_LINE_COLOR;
		this.#maxRadius = maxRadius ?? Number.POSITIVE_INFINITY;
	}

	public dispose(): void {
		if (this.#radiusLine !== null) {
			this.#radiusLine.dispose();
			this.#radiusLine = null;
		}

		this.#centerMarker?.dispose();
		this.#radiusMarker?.dispose();

		if (this.#outline !== null) {
			this.#outline.geometry.dispose();
			this.#outline.material.dispose();
		}

		this.sceneObject.removeFromParent();
		this.sceneObject.clear();

		this.#radiusValue = 0;
		this.#centerPoint = null;
		this.#outline = null;
		this.#centerMarker = null;
		this.#radiusMarker = null;
		this.#radiusDirection.set(1, 0, 0);
	}

	public start({ initialPoint }: { initialPoint: THREE.Vector3 }): void {
		this.#centerMarker = new VertexHandle({ color: this.handleColor });
		this.#radiusMarker = new VertexHandle({ color: this.handleColor });
		this.#outline = this.createOutline();
		this.#radiusLine = new Polyline({ lineColor: this.radiusLineColor, labelColor: this.radiusLineColor });
		this.#radiusLine.line.layers.disableAll();
		this.#radiusLine.line.layers.enable(RENDERABLE_LAYER);
		this.#radiusLine.start({ initialPoint: new THREE.Vector3() });
		this.#radiusLine.update({ currentPoint: new THREE.Vector3(), confirmPoint: true });

		this.sceneObject.position.copy(initialPoint);
		this.sceneObject.add(this.#outline);
		this.sceneObject.add(this.#centerMarker.sceneObject);
		this.sceneObject.add(this.#radiusMarker.sceneObject);
		this.sceneObject.add(this.#radiusLine.sceneObject);

		this.#centerPoint = initialPoint.clone();
		this.#radiusValue = 0;
		this.#radiusDirection.set(1, 0, 0);
		this.syncShapeView();
	}

	public override update(_args: { currentPoint: THREE.Vector3; confirmPoint?: boolean }): void {
		// Circle uses updateRadius() for radius control from the tool.
	}

	public updateRadius({ value, direction }: { value: number; direction: THREE.Vector3 }): void {
		if (this.#centerPoint === null || this.#outline === null) {
			return;
		}

		const planarDirection = new THREE.Vector3(direction.x, 0, direction.z);

		if (planarDirection.lengthSq() > 0) {
			this.#radiusDirection.copy(planarDirection.normalize());
		}

		this.#radiusValue = Math.min(this.#maxRadius, Math.max(0, value));
		this.syncShapeView();
	}

	public setCenter(point: THREE.Vector3): void {
		if (this.#centerPoint === null) {
			return;
		}

		this.#centerPoint.set(point.x, this.#centerPoint.y, point.z);
		this.syncShapeView();
	}

	public setHighlightedHandle(handle: CircleHandle | null): void {
		this.#centerMarker?.update({ isHighlighted: handle === "center" });
		this.#radiusMarker?.update({ isHighlighted: handle === "radius" });
	}

	public getRadiusEnd(): THREE.Vector3 | null {
		if (this.#centerPoint === null) {
			return null;
		}

		return this.#centerPoint.clone().addScaledVector(this.#radiusDirection, this.#radiusValue);
	}

	public override finish(): void {
		this.syncShapeView();
	}

	public override select(): void {}
	public override deselect(): void {}

	protected override syncShapeView(): void {
		if (this.#outline === null || this.#centerPoint === null || this.#centerMarker === null) {
			return;
		}

		this.sceneObject.position.copy(this.#centerPoint);
		this.#outline.scale.set(this.#radiusValue, 1, this.#radiusValue);

		const end = new THREE.Vector3(this.#radiusDirection.x * this.#radiusValue, 0, this.#radiusDirection.z * this.#radiusValue);
		this.#centerMarker.update({ position: new THREE.Vector3() });
		this.#radiusMarker?.update({ position: end });

		if (this.#radiusLine !== null) {
			this.#radiusLine.setPoints({ points: [new THREE.Vector3(0, 0, 0), end] });
		}
	}

	public getCenter(): THREE.Vector3 | null {
		return this.#centerPoint?.clone() ?? null;
	}

	public getRadius(): number {
		return this.#radiusValue;
	}

	private createOutline() {
		const radius = 1;
		const points: Array<THREE.Vector3> = [];

		for (let index = 0; index < OUTLINE_SEGMENTS; index++) {
			const angle = (index / OUTLINE_SEGMENTS) * Math.PI * 2;
			points.push(new THREE.Vector3(Math.cos(angle) * radius, 0, Math.sin(angle) * radius));
		}

		const geometry = new THREE.BufferGeometry().setFromPoints(points);
		const material = new THREE.LineBasicMaterial(this.outlineMaterialDefinition);

		const lineLoop = new THREE.LineLoop(geometry, material);
		lineLoop.frustumCulled = false;

		return lineLoop;
	}
}

export { Circle, type CircleHandle };
